import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  GetUserListParams,
  GetUserListSearchParams,
  ICheckEmail,
  IGetUser,
  IReAssignRecordsOnDeActivateReqDTO,
  IRecordCountSalesTeamIds,
  ITenantUser,
  ITenantUserUpdate,
  IUserEmailUniqueness,
  IUserNotificationsSettings,
  IUserUsageDetails,
} from './typings'
import { ServiceConfig } from '../services/config'
import { APIError, get, post, put, remove } from '../services/crud'
import { ISearchResponse } from '@sales-center/common'
import { IBranchInfoDTO } from '../authorization/typings'
import { IUserReassignOpenRecords } from '@user/components/Settings/Users/ReAssignRecords/hooks/useData'
import { ICheckUniqueEmailResponse } from '@user/components/Settings/Users/UpdateUser/typings'
import { loadFilterPreferences } from '../userPreferences'

const baseUrl = `${ServiceConfig.baseAPIUrl}/Users`

export const getUserList = createAsyncThunk<
  ISearchResponse<ITenantUser>,
  GetUserListParams
>('users/all', async ({ hideGuestUsers, skip, take, orderBy }, thunkApi) => {
  const params = new URLSearchParams()

  if (hideGuestUsers) params.append('hideGuestUsers', 'true')
  if (skip !== undefined) params.append('skip', String(skip))
  if (take !== undefined) params.append('take', String(take))

  if (orderBy && orderBy.trim() !== '') {
    const parts = orderBy.trim().split(/\s+/)

    const sortField = parts[0]
    const sortDirection = parts.length > 1 ? parts[1] : 'asc'

    params.append('sortColumn', sortField)
    params.append('sortDirection', sortDirection)
  }

  const queryString = params.toString()

  const response = await get(
    `${baseUrl}${queryString ? `?${queryString}` : ''}`,
  )
  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getUserListSearch = createAsyncThunk<
  ISearchResponse<ITenantUser>,
  GetUserListSearchParams
>(
  'users/allSearch',
  async ({ hideGuestUsers, skip, take, orderBy, search }, thunkApi) => {
    const params = new URLSearchParams()

    if (search) params.append('search', search)

    if (hideGuestUsers) params.append('hideGuestUsers', 'true')
    if (skip !== undefined) params.append('skip', String(skip))
    if (take !== undefined) params.append('take', String(take))

    if (orderBy && orderBy.trim() !== '') {
      const parts = orderBy.trim().split(/\s+/)

      const sortField = parts[0]
      const sortDirection = parts.length > 1 ? parts[1] : 'asc'

      params.append('sortColumn', sortField)
      params.append('sortDirection', sortDirection)
    }

    const queryString = params.toString()

    const response = await get(
      `${baseUrl}/search${queryString ? `?${queryString}` : ''}`,
    )
    if (response.ok) {
      return await response.json()
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getUserWithCounts = createAsyncThunk<ITenantUser, string>(
  'users/userWithCounts',
  async (id, thunkApi) => {
    const response = await get(`${baseUrl}${`/userWithCounts/${id}`}`)

    if (response.ok) {
      return await response.json()
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getSalesTeamUserList = createAsyncThunk<ITenantUser[], number>(
  'users/salesTeam',
  async (salesTeamId, thunkApi) => {
    const response = await get(`${baseUrl}/salesTeam/${salesTeamId}`)

    if (response.ok) {
      const payload: ISearchResponse<ITenantUser> = await response.json()
      return payload.items
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const createUser = createAsyncThunk<ITenantUser, ITenantUserUpdate>(
  'users/create',
  async (user, thunkApi) => {
    const response = await post(`${baseUrl}`, user)
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getUser = createAsyncThunk<
  ITenantUser,
  IGetUser,
  { rejectValue: string }
>('users/get', async (getUserInput, thunkApi) => {
  const url = getUserInput.getResourceCount
    ? `${baseUrl}/${getUserInput.id}/${getUserInput.getResourceCount}`
    : `${baseUrl}/${getUserInput.id}`
  const response = await get(
    `${url}${getUserInput.checkEmailUnique ? '?checkEmailUnique=true' : ''}`,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getUserBranches = createAsyncThunk<IBranchInfoDTO[]>(
  'users/branches',
  async (_, thunkApi) => {
    const response = await get(`${baseUrl}/branches`)

    if (response.ok) {
      return await response.json()
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const updateUser = createAsyncThunk<
  ITenantUser,
  ITenantUserUpdate,
  { rejectValue: APIError }
>('users/update', async (user, thunkApi) => {
  const response = await put(`${baseUrl}`, user)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteUser = createAsyncThunk<string, string>(
  'users/delete',
  async (id, thunkApi) => {
    const response = await remove(`${baseUrl}/${id}`)
    if (response.ok) {
      thunkApi.dispatch(loadFilterPreferences())
      return id
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getUserEmailUniqueness = createAsyncThunk<IUserEmailUniqueness[]>(
  'users/emailUniqueness',
  async (_, thunkApi) => {
    const response = await get(`${baseUrl}/emailUniqueness`)
    if (response.ok) {
      return await response.json()
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getUsageDetails = createAsyncThunk<IUserUsageDetails, string>(
  'users/usageDetails',
  async (id: string, thunkApi) => {
    const response = await get(`${baseUrl}/usageDetails/${id}`)
    if (response.ok) {
      return await response.json()
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getUserNotificationSettings =
  createAsyncThunk<IUserNotificationsSettings>(
    'users/notificationSettings',
    async (_, thunkApi) => {
      const response = await get(`${baseUrl}/notificationSettings`)
      if (response.ok) {
        return await response.json()
      }
      return thunkApi.rejectWithValue(await response.json())
    },
  )

export const UpdateUserNotificationSettings = createAsyncThunk<
  void,
  IUserNotificationsSettings
>('users/updateNotificationSettings', async (notificationSetting, thunkApi) => {
  const response = await put(
    `${baseUrl}/updateNotificationsState`,
    notificationSetting,
  )
  if (response.ok) {
    return
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getUserRecordsAssignedSalesTeamList = createAsyncThunk<
  IRecordCountSalesTeamIds[],
  IUserReassignOpenRecords
>('users/recordCountSalesTeamIds', async (data, thunkApi) => {
  const response = await get(
    `${baseUrl}/recordCountSalesTeamIds/${data.userId}?reassignLeads=${data.reassignLeads}&reassignOpps=${data.reassignOpps}`,
  )
  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const reAssignRecordsOnDeActivate = createAsyncThunk<
  boolean,
  IReAssignRecordsOnDeActivateReqDTO
>('users/updateOwnerOnDeactivate', async (data, thunkApi) => {
  const response = await post(`${baseUrl}/updateOwnerOnDeactivate`, data)
  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const checkUniqueMail = createAsyncThunk<
  ICheckUniqueEmailResponse,
  ICheckEmail
>('users/checkMail', async (emailData, thunkApi) => {
  const response = await post(`${baseUrl}/checkActiveUniqueMailSC`, emailData)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})
